import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const GLOBAL_ROLES = ['admin', 'qa_manager', 'qa_lead', 'tester', 'viewer'];

const ROLE_BADGES = {
  admin:      { bg: '#fef3c7', color: '#92400e', label: 'Admin' },
  qa_manager: { bg: '#ede9fe', color: '#5b21b6', label: 'QA Manager' },
  qa_lead:    { bg: '#fce7f3', color: '#9d174d', label: 'QA Lead' },
  tester:     { bg: '#dbeafe', color: '#1e40af', label: 'Tester' },
  viewer:     { bg: '#f3f4f6', color: '#374151', label: 'Viewer' },
};

const ROLE_DESC = {
  admin:      'Full system access — manage users, projects, and global settings.',
  qa_manager: 'Manage test runs, edit configurations, assign testers, and manage projects.',
  qa_lead:    'Lead test execution, edit test runs, manage test cases and suites.',
  tester:     'Can create projects and work within assigned projects.',
  viewer:     'Read-only access to projects they are added to.',
};

const EMPTY_FORM = { email: '', password: '', role: 'tester' };

export default function UserManagement() {
  const [users, setUsers]         = useState([]);
  const [isAdmin, setIsAdmin]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [saving, setSaving]       = useState(null);
  const [search, setSearch]       = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [creating, setCreating]   = useState(false);
  const [createdMsg, setCreatedMsg] = useState('');
  const [resetModal, setResetModal]   = useState(null); // { uid, username }
  const [resetPass, setResetPass]     = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMsg, setResetMsg]       = useState('');
  const [deleteModal, setDeleteModal] = useState(null); // { uid, username }
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteSaving, setDeleteSaving]   = useState(false);
  const [openMenu, setOpenMenu]           = useState(null); // { uid, top, right }

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const currentUid = currentUser._id || currentUser.id || '';

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    document.addEventListener('click', close);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [openMenu]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data.data || []);
      setIsAdmin(res.data.isAdmin || false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (userId, newRole) => {
    setSaving(userId);
    try {
      await api.patch(`/auth/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u =>
        (u._id || u.id) === userId ? { ...u, role: newRole } : u
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update role');
    } finally {
      setSaving(null);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const openResetModal = (uid, username) => {
    setResetModal({ uid, username });
    setResetPass(generatePassword());
    setResetMsg('');
  };

  const handleResetPassword = async () => {
    if (!resetPass || resetPass.length < 6) return;
    setResetSaving(true);
    try {
      await api.post(`/auth/users/${resetModal.uid}/reset-password`, { temporaryPassword: resetPass });
      setResetMsg(`✅ Temporary password set for ${resetModal.username}. Share it with them — they must change it on next login.`);
      setUsers(prev => prev.map(u =>
        (u._id || u.id) === resetModal.uid ? { ...u, mustChangePassword: true } : u
      ));
    } catch (err) {
      setResetMsg('❌ ' + (err.response?.data?.message || 'Failed to reset password'));
    } finally {
      setResetSaving(false);
    }
  };
  const handleToggleActive = async (uid, username, currentlyActive) => {
    try {
      await api.patch(`/auth/users/${uid}/status`, { active: !currentlyActive });
      setUsers(prev => prev.map(u =>
        (u._id || u.id) === uid ? { ...u, isActive: !currentlyActive } : u
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal) return;
    setDeleteSaving(true);
    try {
      await api.delete(`/auth/users/${deleteModal.uid}`);
      setUsers(prev => prev.filter(u => (u._id || u.id) !== deleteModal.uid));
      setDeleteModal(null);
      setDeleteConfirm('');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeleteSaving(false);
    }
  };

  const openCreate = () => { setForm(EMPTY_FORM); setFormError(''); setCreatedMsg(''); setShowCreate(true); };
  const closeCreate = () => setShowCreate(false);

  const handleCreate = async e => {
    e.preventDefault();
    setFormError('');
    if (!form.email || !form.password) { setFormError('All fields are required'); return; }
    if (form.password.length < 6) { setFormError('Password must be at least 6 characters'); return; }
    setCreating(true);
    try {
      const username = form.email.split('@')[0].replace(/[^a-zA-Z0-9_]/g, '_');
      const res = await api.post('/auth/users', { ...form, username });
      setUsers(prev => [...prev, res.data.data]);
      setCreatedMsg(`✅ Account created for ${form.email}. Share their email and temporary password so they can sign in.`);
      setForm(EMPTY_FORM);
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const filtered = users.filter(u =>
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/settings">Settings</Link></li>
              <li className="breadcrumb-item active">User Management</li>
            </ol>
          </nav>
          <h1 className="page-title">👤 User Management</h1>
          <p className="page-subtitle">
            {isAdmin
              ? 'Create accounts and manage global roles for all workspace users.'
              : 'View workspace users. Contact an admin to change your role.'}
          </p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary fw-semibold" onClick={openCreate}>
            + Create User
          </button>
        )}
      </div>

      <div className="container-fluid">

        {/* Create User Modal */}
        {showCreate && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)' }} onClick={e => e.target === e.currentTarget && closeCreate()}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">➕ Create User Account</h5>
                  <button type="button" className="btn-close" onClick={closeCreate}></button>
                </div>
                <div className="modal-body pt-2">
                  {createdMsg ? (
                    <div>
                      <div className="alert alert-success" style={{ fontSize: 14 }}>{createdMsg}</div>
                      <button className="btn btn-primary w-100" onClick={closeCreate}>Done</button>
                    </div>
                  ) : (
                    <form onSubmit={handleCreate}>
                      {formError && <div className="alert alert-danger py-2" style={{ fontSize: 14 }}>{formError}</div>}
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Email <span className="text-danger">*</span></label>
                        <input type="email" className="form-control" placeholder="user@company.com" value={form.email}
                          onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required autoFocus />
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Temporary Password <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" placeholder="Min. 6 characters" value={form.password}
                          onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required minLength={6} />
                        <small className="text-muted">User will be required to change this on first login.</small>
                      </div>
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Global Role</label>
                        <select className="form-select" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                          {GLOBAL_ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                        </select>
                      </div>
                      <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary flex-shrink-0" onClick={closeCreate}>Cancel</button>
                        <button type="submit" className="btn btn-primary flex-grow-1 fw-semibold" disabled={creating}>
                          {creating ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</> : 'Create Account'}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetModal && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)' }} onClick={e => e.target === e.currentTarget && setResetModal(null)}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">🔑 Reset Password — {resetModal.username}</h5>
                  <button type="button" className="btn-close" onClick={() => setResetModal(null)} />
                </div>
                <div className="modal-body">
                  {resetMsg ? (
                    <div>
                      <div className={`alert ${resetMsg.startsWith('✅') ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: 14 }}>{resetMsg}</div>
                      <button className="btn btn-primary w-100" onClick={() => setResetModal(null)}>Done</button>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-3">
                        Set a temporary password for this user. They will be required to choose a new password immediately after logging in.
                      </p>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Temporary Password</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control font-monospace"
                            value={resetPass}
                            onChange={e => setResetPass(e.target.value)}
                            minLength={6}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            title="Generate new password"
                            onClick={() => setResetPass(generatePassword())}
                          >🔄</button>
                        </div>
                        <small className="text-muted">Min. 6 characters. Share this with the user securely.</small>
                      </div>
                      <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary flex-shrink-0" onClick={() => setResetModal(null)}>Cancel</button>
                        <button
                          type="button"
                          className="btn btn-warning flex-grow-1 fw-semibold"
                          disabled={resetSaving || resetPass.length < 6}
                          onClick={handleResetPassword}
                        >
                          {resetSaving ? <><span className="spinner-border spinner-border-sm me-2" />Resetting…</> : '🔑 Set Temporary Password'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Modal */}
        {deleteModal && (
          <div className="modal d-block" style={{ background: 'rgba(0,0,0,.5)' }} onClick={e => e.target === e.currentTarget && setDeleteModal(null)}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold text-danger">🗑️ Delete User — {deleteModal.username}</h5>
                  <button type="button" className="btn-close" onClick={() => { setDeleteModal(null); setDeleteConfirm(''); }} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 14 }}>
                    ⚠️ This action is <strong>permanent and cannot be undone</strong>. All data associated with this user will be removed.
                  </div>
                  <p className="text-muted small mb-3">
                    Type <strong>{deleteModal.username}</strong> to confirm deletion:
                  </p>
                  <input
                    type="text"
                    className="form-control mb-3"
                    placeholder={deleteModal.username}
                    value={deleteConfirm}
                    onChange={e => setDeleteConfirm(e.target.value)}
                    autoFocus
                  />
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary flex-shrink-0" onClick={() => { setDeleteModal(null); setDeleteConfirm(''); }}>Cancel</button>
                    <button
                      type="button"
                      className="btn btn-danger flex-grow-1 fw-semibold"
                      disabled={deleteConfirm !== deleteModal.username || deleteSaving}
                      onClick={handleDeleteUser}
                    >
                      {deleteSaving ? <><span className="spinner-border spinner-border-sm me-2" />Deleting…</> : '🗑️ Delete User'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Role legend */}
        <div className="card mb-4">
          <div className="card-body py-3">
            <div className="row g-3">
              {GLOBAL_ROLES.map(r => (
                <div className="col-md-4" key={r}>
                  <div className="d-flex align-items-start gap-2">
                    <span className="badge mt-1 flex-shrink-0" style={{ background: ROLE_BADGES[r].bg, color: ROLE_BADGES[r].color, fontSize: 12, padding: '4px 10px' }}>
                      {ROLE_BADGES[r].label}
                    </span>
                    <p className="text-muted small mb-0">{ROLE_DESC[r]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {!isAdmin && !loading && !error && (
          <div className="alert alert-info d-flex align-items-center gap-2 mb-4">
            <span style={{ fontSize: 18 }}>ℹ️</span>
            <span>You have <strong>read-only</strong> access to this page. Only system admins can change user roles.</span>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}

        {!error && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: 320 }}
                placeholder="Search by name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {!loading && (
                <span className="text-muted small">
                  {filtered.length} user{filtered.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            ) : (
              <div className="card">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Global Role</th>
                      <th>Joined</th>
                      {isAdmin && <th style={{ width: 220 }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => {
                      const uid          = u._id || u.id;
                      const isSelf       = String(uid) === String(currentUid);
                      const isSuperAdmin = !!u.isSuperAdmin;
                      const isActive     = u.isActive !== false; // default true if field absent
                      const badge        = ROLE_BADGES[u.role] || ROLE_BADGES.viewer;
                      return (
                        <tr key={uid}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div style={{
                                width: 34, height: 34, borderRadius: '50%',
                                background: isSuperAdmin
                                  ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                                  : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                color: '#fff', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0,
                              }}>
                                {isSuperAdmin ? '👑' : (u.username?.slice(0, 2).toUpperCase() || '?')}
                              </div>
                              <div>
                                <span className="fw-semibold">{u.username}</span>
                                {isSelf && (
                                  <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>You</span>
                                )}
                                {isSuperAdmin && (
                                  <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: 10 }}>Super Admin</span>
                                )}
                                {u.mustChangePassword && (
                                  <span className="badge ms-1" style={{ background: '#fef3c7', color: '#92400e', fontSize: 10 }}>Temp password</span>
                                )}
                                {!isActive && (
                                  <span className="badge ms-1" style={{ background: '#fee2e2', color: '#991b1b', fontSize: 10 }}>Inactive</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-muted">{u.email}</td>
                          <td>
                            <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12, padding: '5px 10px' }}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="text-muted small">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                          </td>
                          {isAdmin && (
                            <td>
                              {isSelf ? (
                                <span className="text-muted small">—</span>
                              ) : isSuperAdmin ? (
                                <span className="text-muted small">🔒 Protected</span>
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ width: 130 }}
                                    value={u.role}
                                    disabled={saving === uid}
                                    onChange={e => changeRole(uid, e.target.value)}
                                  >
                                    {GLOBAL_ROLES.map(r => (
                                      <option key={r} value={r}>
                                        {r.charAt(0).toUpperCase() + r.slice(1)}
                                      </option>
                                    ))}
                                  </select>
                                  {saving === uid && (
                                    <div className="spinner-border spinner-border-sm text-primary" />
                                  )}
                                  <div style={{ position: 'relative' }}>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      title="More actions"
                                      onClick={e => {
                                        e.stopPropagation();
                                        if (openMenu?.uid === uid) { setOpenMenu(null); return; }
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setOpenMenu({ uid, top: rect.bottom, left: rect.right - 180 });
                                      }}
                                    >⋮</button>
                                    {openMenu?.uid === uid && ReactDOM.createPortal(
                                      <ul
                                        className="dropdown-menu show"
                                        style={{ position: 'fixed', top: openMenu.top, left: Math.max(4, openMenu.left), zIndex: 9999, minWidth: 180 }}
                                        onClick={e => e.stopPropagation()}
                                      >
                                        <li>
                                          <button className="dropdown-item" onClick={() => { setOpenMenu(null); openResetModal(uid, u.username); }}>
                                            🔑 Reset Password
                                          </button>
                                        </li>
                                        <li>
                                          <button className="dropdown-item" onClick={() => { setOpenMenu(null); handleToggleActive(uid, u.username, isActive); }}>
                                            {isActive ? '🚫 Deactivate' : '✅ Activate'}
                                          </button>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                          <button className="dropdown-item text-danger" onClick={() => { setOpenMenu(null); setDeleteModal({ uid, username: u.username }); setDeleteConfirm(''); }}>
                                            🗑️ Delete User
                                          </button>
                                        </li>
                                      </ul>,
                                      document.body
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={isAdmin ? 5 : 4} className="text-center text-muted py-4">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
